/**
 * Sidebar control: device picker + permission state UX for the mic.
 *
 * Three rendering branches based on `MicStore.snapshot.permission`:
 *
 *   "prompt"       → "Enable voice" button; click triggers OS prompt.
 *   "granted"      → device <select> populated with labelled inputs;
 *                    "Default" entry maps to deviceId=null.
 *   "denied"       → inline help with browser unblock instructions.
 *   "unsupported"  → static notice (older browsers / insecure context).
 *
 * The component owns no audio state — it observes MicStore and writes
 * back the user's picker selection. `VoiceConnection` consumes
 * `MicStore.snapshot.activeMicId` when it opens the mic.
 */

import { MicStore, type MicStoreState } from "@/audio/MicStore";

export class MicSettings {
  private root: HTMLElement;
  private dispose: (() => void) | null = null;

  constructor(root: HTMLElement) {
    this.root = root;
  }

  async init(): Promise<void> {
    this.render(MicStore.snapshot);
    this.dispose = MicStore.onChange((state) => this.render(state));

    // Kick off enumeration without prompting; result re-renders.
    void MicStore.init();
  }

  destroy(): void {
    this.dispose?.();
    this.dispose = null;
    this.root.innerHTML = "";
  }

  private render(state: MicStoreState): void {
    if (state.permission === "unsupported") {
      this.root.innerHTML = `
        <div class="mic-notice">
          Voice not supported on this browser or insecure connection.
        </div>
      `;
      return;
    }

    if (state.permission === "denied") {
      const helpUrl = browserMicHelpUrl();
      this.root.innerHTML = `
        <div class="mic-notice mic-notice-denied">
          Microphone blocked.
          <a class="mic-help-link" href="${helpUrl}" target="_blank" rel="noopener noreferrer">
            How to re-enable
          </a>
        </div>
      `;
      return;
    }

    if (state.permission === "prompt") {
      this.root.innerHTML = `
        <button id="mic-enable-btn" type="button" class="mic-enable">
          🎙 Enable voice
        </button>
      `;
      const btn = this.root.querySelector<HTMLButtonElement>("#mic-enable-btn");
      btn?.addEventListener("click", () => {
        void MicStore.requestPermissionAndEnumerate();
      });
      return;
    }

    // permission === "granted"
    const { available, activeMicId } = state;

    const optionsHtml = [
      `<option value="">Default</option>`,
      ...available.map(
        (d) => `
          <option value="${escapeAttr(d.deviceId)}" ${d.deviceId === activeMicId ? "selected" : ""}>
            ${escapeText(d.label || "(unlabelled mic)")}
          </option>
        `,
      ),
    ].join("");

    this.root.innerHTML = `
      <label class="mic-label" for="mic-device-select">Microphone</label>
      <select id="mic-device-select" class="mic-select">
        ${optionsHtml}
      </select>
    `;

    const select = this.root.querySelector<HTMLSelectElement>("#mic-device-select");
    select?.addEventListener("change", () => {
      const value = select.value;
      MicStore.setActiveMic(value === "" ? null : value);
    });
  }
}

function escapeText(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function escapeAttr(s: string): string {
  return escapeText(s).replace(/"/g, "&quot;");
}

// Browser-specific deep-link to per-site permission settings. Falls
// back to the support page if we don't recognise the UA.
function browserMicHelpUrl(): string {
  const ua = navigator.userAgent;

  if (/Firefox\//.test(ua)) {
    return "https://support.mozilla.org/kb/how-manage-your-camera-and-microphone-permissions";
  }

  if (/Edg\//.test(ua)) {
    return "https://support.microsoft.com/microsoft-edge/microsoft-edge-camera-microphone-and-location-permissions-fe17c0c0-9bc5-4f6f-83e4-f4eddf64dec6";
  }

  if (/Chrome\//.test(ua)) {
    return "https://support.google.com/chrome/answer/2693767";
  }

  if (/Safari\//.test(ua)) {
    return "https://support.apple.com/guide/safari/websites-ibrwe2159f50/mac";
  }

  return "https://webrtc.org/getting-started/microphone-camera";
}
